/**
 * Draws Skyline Stacker on a canvas: sky, a light skyline, the ground, the
 * tower, the sliding brick and falling pieces (look from the design's Tower part).
 */
import { drawIsoBrick, isoMetrics } from "../../isoBrick";
import type { Floor } from "./engine";
import { FIELD_WIDTH } from "./world";
import { Camera, Debris, floorTop, movingTop, VIEW_STUDS } from "./scene";

export const FLOOR_COLORS = ["#d8402f", "#f0b72f", "#2f5fd0", "#2f9a55", "#2a2724"];
export const floorColor = (index: number) => FLOOR_COLORS[index % FLOOR_COLORS.length];

// Background buildings: [left edge, width, height] in studs (from the design).
const SKYLINE: [number, number, number][] = [
  [-7, 4.5, 7],
  [-2.5, 4.6, 5.8],
  [2.7, 2.7, 8.8],
  [5.4, 3.5, 6.5],
  [11.5, 3.1, 10],
  [14.2, 4.2, 5.4],
  [18, 3.5, 7.7],
  [21.2, 4.6, 6.2],
];

export interface SceneFrame {
  floors: Floor[];
  moving: Floor | null;
  camera: Camera;
  debris: Debris[];
}

/** Draws one frame. `width`/`height` are CSS pixels; the context is already scaled for the screen. */
export function drawScene(ctx: CanvasRenderingContext2D, frame: SceneFrame, width: number, height: number): void {
  const { camera } = frame;
  const unit = width / VIEW_STUDS / camera.zoom;
  const { dx } = isoMetrics(unit);
  const left = (width - FIELD_WIDTH * unit - dx) / 2;
  const sx = (x: number) => left + x * unit;
  const sy = (y: number) => height - (y - camera.bottom) * unit;

  ctx.clearRect(0, 0, width, height);
  ctx.fillStyle = "#e9e3d6";
  ctx.fillRect(0, 0, width, height);

  // Skyline behind the tower.
  SKYLINE.forEach(([x, w, h], i) => {
    const top = sy(h), bottom = sy(0);
    if (bottom < 0 || top > height) return;
    ctx.fillStyle = i % 2 ? "#ddd4c2" : "#e2dac9";
    ctx.fillRect(sx(x), top, w * unit, bottom - top);
    ctx.fillStyle = "#ebe5d8";
    const s = unit * 0.3;
    for (let wy = h - 0.6; wy > 0.9; wy -= 1) {
      for (let wx = x + 0.55; wx < x + w - 0.6; wx += 0.92) ctx.fillRect(sx(wx), sy(wy), s, s);
    }
  });

  // Ground.
  const ground = sy(0);
  if (ground < height) {
    ctx.fillStyle = "#2a2724";
    ctx.fillRect(0, ground, width, height - ground);
  }

  // Tower, bottom first (each floor's top face is covered by the one above).
  frame.floors.forEach((f, i) => {
    const y = sy(floorTop(i));
    if (y > height + unit || y < -unit * 3) return;
    drawIsoBrick(ctx, { x: sx(f.x), y, len: f.w * unit, color: floorColor(i) }, unit);
  });

  if (frame.moving) {
    const n = frame.floors.length;
    drawIsoBrick(ctx, { x: sx(frame.moving.x), y: sy(movingTop(n)), len: frame.moving.w * unit, color: floorColor(n) }, unit);
  }

  frame.debris.forEach((d) => {
    const len = d.w * unit;
    const cx = sx(d.x) + len / 2, cy = sy(d.y) + unit * 0.55;
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(d.rot);
    drawIsoBrick(ctx, { x: -len / 2, y: -unit * 0.55, len, color: d.color }, unit);
    ctx.restore();
  });
}
